import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
} from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import {
  FaEdit,
  FaEye,
} from 'react-icons/fa';

import SmartTable from '../../components/Smart/SmartTable';
import SmartTopTool from '../../components/Smart/SmartTopTool';
import SmartPagination from '../../components/Smart/SmartPagination';

import API from '../../api/axios';
import useCompanyContext from '../../hooks/useCompanyContext';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_VISIBLE_DESTINATIONS = 3;

const INITIAL_META = {
  total: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalPages: 1,
};

const INITIAL_PAGE = {
  page_number: 1,
  total_pages: 1,
};

const INITIAL_EXACT_SEARCH = {
  originShopName: '',
  originLocation: '',
  destinationShopName: '',
  destinationLocation: '',
};

const EXACT_SEARCH_FIELDS = [
  {
    key: 'originShopName',
    label: 'Pick-up shop',
    placeholder:
      'Exact pick-up shop name',
  },
  {
    key: 'originLocation',
    label: 'Pick-up location',
    placeholder:
      'Exact pick-up location',
  },
  {
    key: 'destinationShopName',
    label: 'Destination shop',
    placeholder:
      'Exact destination shop name',
  },
  {
    key: 'destinationLocation',
    label: 'Destination location',
    placeholder:
      'Exact destination location',
  },
];

const normalizeLocation = (
  location,
  fallbackName,
) => {
  if (
    typeof location ===
    'string'
  ) {
    return {
      id: null,
      name: location,
      slug: '',
      cityName: '',
    };
  }

  const source =
    location || {};

  return {
    id:
      source.id ??
      null,

    name:
      source.name ??
      source.locationName ??
      fallbackName ??
      '',

    slug:
      source.slug ??
      '',

    cityName:
      source.cityName ??
      source.City?.name ??
      source.city?.name ??
      '',
  };
};

const normalizeShop = (
  source,
) => {
  const shop =
    source?.destinationShop ||
    source?.DestinationShop ||
    source?.Shop ||
    source?.shop ||
    source ||
    {};

  const location =
    normalizeLocation(
      shop.Location ||
      shop.location ||
      source?.Location ||
      source?.location,
      source?.locationName,
    );

  return {
    id:
      shop.id ??
      source?.shopId ??
      source?.destinationShopId ??
      null,

    name:
      shop.name ??
      source?.shopName ??
      source?.destinationShopName ??
      '',

    location,

    cityName:
      location.cityName ||
      source?.cityName ||
      '',

    isActive:
      source?.isEnabled ??
      source?.isActive ??
      source?.enabled ??
      true,

    priceCents:
      source?.priceCents ??
      source?.price ??
      source?.dropOffPriceCents ??
      null,

    currency:
      source?.currency ??
      'EUR',

    raw:
      source,
  };
};

const getDestinationSource = (
  group,
) => {
  const candidates = [
    group?.destinations,
    group?.destinationShops,
    group?.shops,
    group?.dropOffConfigs,
    group?.ShopDropoffConfig,
    group?.ShopDropOffConfig,
    group?.items,
  ];

  return (
    candidates.find(
      Array.isArray,
    ) || []
  );
};

const normalizeGroup = (
  group,
) => {
  const originSource =
    group?.originShop ||
    group?.OriginShop ||
    group?.shop ||
    group?.Shop ||
    {};

  const originShop =
    normalizeShop(
      originSource,
    );

  const destinations =
    getDestinationSource(
      group,
    )
      .map(
        normalizeShop,
      )
      .filter(
        (destination) =>
          destination.id ||
          destination.name,
      );

  const enabledDestinationCount =
    group?.enabledDestinationCount ??
    destinations.filter(
      (destination) =>
        destination.isActive !==
        false,
    ).length;

  const disabledDestinationCount =
    group?.disabledDestinationCount ??
    destinations.filter(
      (destination) =>
        destination.isActive ===
        false,
    ).length;

  const destinationPrices =
    destinations
      .map(
        (destination) =>
          Number(
            destination.priceCents,
          ),
      )
      .filter(
        Number.isFinite,
      );

  return {
    ...group,

    id:
      originShop.id ||
      group?.id,

    originShop,
    destinations,

    destinationCount:
      group?.destinationCount ??
      destinations.length,

    enabledDestinationCount,
    disabledDestinationCount,

    minPriceCents:
      group?.minPriceCents ??
      (
        destinationPrices.length
          ? Math.min(
            ...destinationPrices,
          )
          : 0
      ),

    maxPriceCents:
      group?.maxPriceCents ??
      (
        destinationPrices.length
          ? Math.max(
            ...destinationPrices,
          )
          : 0
      ),

    currency:
      group?.currency ??
      destinations[0]
        ?.currency ??
      'EUR',

    updatedAt:
      group?.updatedAt ??
      null,
  };
};

const formatMoney = (
  cents,
  currency = 'EUR',
) => {
  const normalized =
    Number(cents);

  const amount =
    Number.isFinite(
      normalized,
    )
      ? normalized / 100
      : 0;

  return `${amount.toFixed(2)} ${currency}`;
};

const renderShopLocation = (
  shop,
  badgeVariant = 'primary',
) => {
  const locationLabel =
    shop?.cityName ||
    shop?.location?.cityName ||
    shop?.location?.name ||
    shop?.location?.slug ||
    'No location';

  return (
    <div className="d-flex align-items-center gap-2 py-1">
      <Badge
        bg={badgeVariant}
        className="text-truncate"
        style={{
          maxWidth: 160,
          minWidth: 100,
        }}
        title={locationLabel}
      >
        {locationLabel}
      </Badge>

      <span
        className="text-truncate"
        title={
          shop?.name ||
          ''
        }
      >
        {shop?.name ||
          '-'}
      </span>
    </div>
  );
};

const DropOffList = () => {
  const {
    currentCompanyID,
    hasSpecificCompany,
    buildCompanyUrl,
  } = useCompanyContext();

  const supplierId =
    hasSpecificCompany
      ? String(
        currentCompanyID,
      )
      : '';

  const [
    tableData,
    setTableData,
  ] = useState([]);

  const [meta, setMeta] =
    useState(
      INITIAL_META,
    );

  const [page, setPage] =
    useState(
      INITIAL_PAGE,
    );

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    globalSearch,
    setGlobalSearch,
  ] = useState('');

  const [
    exactSearch,
    setExactSearch,
  ] = useState(
    INITIAL_EXACT_SEARCH,
  );

  const [
    expandedOriginShopIds,
    setExpandedOriginShopIds,
  ] = useState(
    () => new Set(),
  );

  const requestRef =
    useRef(0);

  const previousCompanyIdRef =
    useRef(
      currentCompanyID,
    );

  const skipNextFetchRef =
    useRef(false);

  const fetchDropoffList =
    useCallback(
      async () => {
        const requestId =
          ++requestRef.current;

        try {
          setLoading(true);
          setError('');

          const params =
            new URLSearchParams();

          params.set(
            'page',
            String(
              page.page_number ||
              1,
            ),
          );

          params.set(
            'pageSize',
            String(
              meta.pageSize ||
              DEFAULT_PAGE_SIZE,
            ),
          );

          const normalizedGlobalSearch =
            String(
              globalSearch ||
              '',
            ).trim();

          if (
            normalizedGlobalSearch
          ) {
            params.set(
              'q',
              normalizedGlobalSearch,
            );
          }

          Object.entries(
            exactSearch,
          ).forEach(
            ([
              key,
              value,
            ]) => {
              const normalized =
                String(
                  value ||
                  '',
                ).trim();

              if (
                normalized
              ) {
                params.set(
                  key,
                  normalized,
                );
              }
            },
          );

          /*
           * ALL companies 时不传 supplierId。
           */
          if (supplierId) {
            params.set(
              'supplierId',
              supplierId,
            );
          }

          const {
            data,
          } =
            await API.get(
              `/shop-dropoff-config?${params.toString()}`,
            );

          if (
            requestId !==
            requestRef.current
          ) {
            return;
          }

          const normalizedRows =
            (
              Array.isArray(
                data?.data,
              )
                ? data.data
                : []
            ).map(
              normalizeGroup,
            );

          setTableData(
            normalizedRows,
          );

          const responseMeta =
            data?.meta ||
            data?.pagination ||
            {};

          const nextMeta = {
            total:
              responseMeta.total ??
              data?.total ??
              0,

            page:
              responseMeta.page ??
              data?.page ??
              1,

            pageSize:
              responseMeta.pageSize ??
              data?.pageSize ??
              DEFAULT_PAGE_SIZE,

            totalPages:
              responseMeta.totalPages ??
              data?.totalPages ??
              1,
          };

          setMeta(
            nextMeta,
          );

          setPage(
            (prev) => {
              if (
                prev.page_number ===
                nextMeta.page &&
                prev.total_pages ===
                nextMeta.totalPages
              ) {
                return prev;
              }

              return {
                ...prev,

                page_number:
                  nextMeta.page,

                total_pages:
                  nextMeta.totalPages,
              };
            },
          );
        } catch (
        requestError
        ) {
          if (
            requestId !==
            requestRef.current
          ) {
            return;
          }

          setError(
            requestError
              ?.response
              ?.data
              ?.message ||
            requestError?.message ||
            'Failed to load drop-off configurations',
          );

          setTableData([]);

          setMeta(
            INITIAL_META,
          );

          setPage(
            (prev) => ({
              ...prev,
              total_pages: 1,
            }),
          );
        } finally {
          if (
            requestId ===
            requestRef.current
          ) {
            setLoading(
              false,
            );
          }
        }
      },
      [
        page.page_number,
        meta.pageSize,
        globalSearch,
        exactSearch,
        supplierId,
      ],
    );

  const handleSetGlobalSearch =
    useCallback(
      (
        valueOrUpdater,
      ) => {
        setGlobalSearch(
          (prev) =>
            typeof valueOrUpdater ===
              'function'
              ? valueOrUpdater(
                prev,
              )
              : valueOrUpdater,
        );

        setExpandedOriginShopIds(
          new Set(),
        );

        setPage(
          (prev) =>
            prev.page_number ===
              1
              ? prev
              : {
                ...prev,
                page_number:
                  1,
              },
        );
      },
      [],
    );

  const handleSetExactSearch =
    useCallback(
      (
        valueOrUpdater,
      ) => {
        setExactSearch(
          (prev) =>
            typeof valueOrUpdater ===
              'function'
              ? valueOrUpdater(
                prev,
              )
              : valueOrUpdater,
        );

        setExpandedOriginShopIds(
          new Set(),
        );

        setPage(
          (prev) =>
            prev.page_number ===
              1
              ? prev
              : {
                ...prev,
                page_number:
                  1,
              },
        );
      },
      [],
    );

  const toggleExpanded =
    useCallback(
      (
        originShopId,
      ) => {
        setExpandedOriginShopIds(
          (previous) => {
            const next =
              new Set(
                previous,
              );

            if (
              next.has(
                originShopId,
              )
            ) {
              next.delete(
                originShopId,
              );
            } else {
              next.add(
                originShopId,
              );
            }

            return next;
          },
        );
      },
      [],
    );

  /*
   * 切 company：
   * - 清搜索
   * - 清展开
   * - 回第一页
   */
  useEffect(() => {
    if (
      previousCompanyIdRef
        .current ===
      currentCompanyID
    ) {
      return;
    }

    previousCompanyIdRef.current =
      currentCompanyID;

    setGlobalSearch('');

    setExactSearch(
      INITIAL_EXACT_SEARCH,
    );

    setExpandedOriginShopIds(
      new Set(),
    );

    if (
      page.page_number !==
      1
    ) {
      skipNextFetchRef.current =
        true;

      setPage(
        (prev) => ({
          ...prev,
          page_number: 1,
        }),
      );
    }
  }, [
    currentCompanyID,
    page.page_number,
  ]);

  useEffect(() => {
    if (
      skipNextFetchRef.current
    ) {
      skipNextFetchRef.current =
        false;

      return;
    }

    fetchDropoffList();
  }, [fetchDropoffList]);

  const columns =
    useMemo(
      () => [
        {
          keys: [
            'originShop',
          ],

          label:
            'Pick Up',

          render:
            (item) =>
              renderShopLocation(
                item.originShop,
                'primary',
              ),
        },

        {
          keys: [
            'destinations',
            'destinationCount',
          ],

          label:
            'Destinations',

          render:
            (item) => {
              const rowId =
                item.originShop
                  ?.id ||
                item.id;

              const expanded =
                expandedOriginShopIds.has(
                  rowId,
                );

              const destinations =
                Array.isArray(
                  item.destinations,
                )
                  ? item.destinations
                  : [];

              const visibleDestinations =
                expanded
                  ? destinations
                  : destinations.slice(
                    0,
                    DEFAULT_VISIBLE_DESTINATIONS,
                  );

              const hasMore =
                destinations.length >
                DEFAULT_VISIBLE_DESTINATIONS;

              if (
                destinations.length ===
                0
              ) {
                return (
                  <span className="text-muted">
                    No destinations
                  </span>
                );
              }

              return (
                <div>
                  {visibleDestinations.map(
                    (
                      destination,
                      index,
                    ) => (
                      <div
                        key={
                          destination.id ||
                          `${rowId}-${index}`
                        }
                        className="d-flex align-items-center justify-content-between gap-3"
                      >
                        <div className="flex-grow-1 overflow-hidden">
                          {renderShopLocation(
                            destination,
                            destination.isActive ===
                              false
                              ? 'secondary'
                              : 'success',
                          )}
                        </div>
                      </div>
                    ),
                  )}

                  {hasMore && (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="px-0 mt-1 text-decoration-none"
                      onClick={(
                        event,
                      ) => {
                        event.preventDefault();
                        event.stopPropagation();

                        toggleExpanded(
                          rowId,
                        );
                      }}
                    >
                      {expanded
                        ? 'Show less'
                        : `Show all ${destinations.length} destinations`}
                    </Button>
                  )}
                </div>
              );
            },
        },

        {
          key: 'prices',
          label: 'Prices',

          render:
            (item) => {
              const rowId =
                item.originShop
                  ?.id ||
                item.id;

              const expanded =
                expandedOriginShopIds.has(
                  rowId,
                );

              const destinations =
                Array.isArray(
                  item.destinations,
                )
                  ? item.destinations
                  : [];

              const visibleDestinations =
                expanded
                  ? destinations
                  : destinations.slice(
                    0,
                    DEFAULT_VISIBLE_DESTINATIONS,
                  );

              if (
                !visibleDestinations.length
              ) {
                return (
                  <span className="text-muted">
                    -
                  </span>
                );
              }

              return (
                <div>
                  {visibleDestinations.map(
                    (
                      destination,
                      index,
                    ) => (
                      <div
                        key={
                          destination.id ||
                          `${rowId}-price-${index}`
                        }
                        className="py-1 text-nowrap"
                      >
                        {destination.priceCents !==
                          null &&
                          destination.priceCents !==
                          undefined
                          ? formatMoney(
                            destination.priceCents,
                            destination.currency,
                          )
                          : '-'}
                      </div>
                    ),
                  )}
                </div>
              );
            },
        },

        {
          key:
            'actions',

          label:
            'Actions',

          render:
            (item) => {
              const originShopId =
                item.originShop
                  ?.id;

              if (
                !originShopId
              ) {
                return '-';
              }

              return (
                <div
                  className="d-flex gap-2 align-items-center"
                  onClick={(
                    event,
                  ) =>
                    event.stopPropagation()
                  }
                >
                  <NavLink
                    to={buildCompanyUrl(
                      `/dropoff/view/${originShopId}`,
                    )}
                    className="btn btn-view p-0"
                    title="View"
                  >
                    <FaEye
                      size={20}
                    />
                  </NavLink>

                  <NavLink
                    to={buildCompanyUrl(
                      `/dropoff/edit/${originShopId}`,
                    )}
                    className="btn btn-edit p-0"
                    title="Edit"
                  >
                    <FaEdit
                      size={20}
                      style={{
                        marginLeft:
                          4,
                      }}
                    />
                  </NavLink>
                </div>
              );
            },
        },
      ],
      [
        buildCompanyUrl,
        expandedOriginShopIds,
        toggleExpanded,
      ],
    );

  return (
    <Container
      fluid
      className="d-flex flex-column"
    >
      <Row className="mb-4">
        <Col sm={8}>
          <span className="fw-semibold">
            DropOff List
          </span>
        </Col>

        <Col
          sm={4}
          className="text-end"
        >
          <span className="fw-semibold">
            <NavLink
              to={buildCompanyUrl(
                '/dashboard',
              )}
              className="block-link"
            >
              Dashboard
            </NavLink>

            {' - DropOff List'}
          </span>
        </Col>
      </Row>

      <Row>
        <Col sm={12}>
          <Card className="h-100 p-2">
            <Card.Body className="px-2 pt-2 pb-5">
              <Row>
                <Col md={10}>
                  <SmartTopTool
                    advancedSearch
                    globalSearch={
                      globalSearch
                    }
                    setGlobalSearch={
                      handleSetGlobalSearch
                    }
                    globalSearchPlaceholder="Search pick-up or destination shop and location..."
                    exactSearch={
                      exactSearch
                    }
                    setExactSearch={
                      handleSetExactSearch
                    }
                    exactSearchFields={
                      EXACT_SEARCH_FIELDS
                    }
                    debounceMs={
                      400
                    }
                  />
                </Col>

                {supplierId && (
                  <Col
                    md={2}
                    className="d-flex justify-content-end align-items-start"
                  >
                    <NavLink
                      to={buildCompanyUrl(
                        '/dropoff/new',
                      )}
                      className="border ms-1 btn-custom-success btn btn-primary"
                      title="New"
                      style={{
                        height: 40,
                      }}
                    >
                      + New
                    </NavLink>
                  </Col>
                )}

                <Col md={12}>
                  <SmartTable
                    data={
                      tableData
                    }
                    columns={
                      columns
                    }
                    loading={
                      loading
                    }
                    emptyState={
                      <div className="text-center py-5 text-muted">
                        <p>
                          {error ||
                            'No matching drop-off configurations found'}
                        </p>
                      </div>
                    }
                  />
                </Col>

                <Col
                  md={12}
                  className="d-flex justify-content-end"
                >
                  <SmartPagination
                    page={page}
                    setPage={
                      setPage
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default DropOffList;